import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { getCartSessionId } from '@/stores/cart-session';
import type { ApiErrorBody, ApiErrorCode, ApiSuccess, Paginated } from '@/types/api';

export const CART_SESSION_HEADER = 'X-Cart-Session';
export const DEVICE_HEADER = 'X-Device-Id';

const DEVICE_STORAGE_KEY = 'hc.deviceId';

/** Misafir favorilerinin cihaz kimliği; bir kez üretilir, korunur. */
export function getDeviceId(): string {
  const existing = localStorage.getItem(DEVICE_STORAGE_KEY);

  if (existing && /^[A-Za-z0-9_-]{16,64}$/.test(existing)) {
    return existing;
  }

  const created = crypto.randomUUID().replace(/-/g, '');
  localStorage.setItem(DEVICE_STORAGE_KEY, created);
  return created;
}

/**
 * TEK merkezi HTTP istemcisi. Component'ler kendi axios örneğini yaratmaz.
 * Base URL, token, yenileme ve ortak hata çevirisi yalnızca burada yaşar.
 */

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

const ACCESS_TOKEN_KEY = 'hc.accessToken';
const REFRESH_TOKEN_KEY = 'hc.refreshToken';

export const tokenStorage = {
  getAccess: (): string | null => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefresh: (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY),
  set: (accessToken: string, refreshToken: string): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },
  clear: (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

/**
 * Uygulama genelinde tek hata tipi. Component'ler AxiosError ayıklamaz.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly fields?: Record<string, string[]>;

  constructor(params: {
    code: ApiErrorCode;
    message: string;
    status: number;
    fields?: Record<string, string[]>;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.code = params.code;
    this.status = params.status;
    this.fields = params.fields;
  }

  /** Form altında gösterilecek alan hatası var mı? */
  get isValidation(): boolean {
    return this.code === 'VALIDATION_FAILED';
  }

  get isAuth(): boolean {
    return this.code === 'UNAUTHORIZED';
  }

  get isNetwork(): boolean {
    return this.code === 'NETWORK';
  }
}

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccess();

  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  // Misafir sepetinin kimliği her istekte gider; sepet backend'de yaşar.
  config.headers.set(CART_SESSION_HEADER, getCartSessionId());

  // Misafir favorileri cihaz kimliğine bağlıdır; girişte hesaba taşınır.
  config.headers.set(DEVICE_HEADER, getDeviceId());

  return config;
});

// --- Token yenileme -------------------------------------------------------
// Aynı anda 401 alan birden fazla istek tek bir yenileme çağrısını bekler.

let refreshPromise: Promise<string | null> | null = null;
const authFailureListeners = new Set<() => void>();

export function onAuthFailure(listener: () => void): () => void {
  authFailureListeners.add(listener);
  return () => authFailureListeners.delete(listener);
}

function notifyAuthFailure(): void {
  tokenStorage.clear();
  authFailureListeners.forEach((listener) => listener());
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = tokenStorage.getRefresh();

  if (!refreshToken) {
    return null;
  }

  try {
    const response = await axios.post<
      ApiSuccess<{ accessToken: string; refreshToken: string }>
    >(`${BASE_URL}/auth/refresh`, { refreshToken });

    const { accessToken, refreshToken: nextRefresh } = response.data.data;
    tokenStorage.set(accessToken, nextRefresh);
    return accessToken;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;

    const isAuthEndpoint = original?.url?.includes('/auth/');

    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !isAuthEndpoint &&
      tokenStorage.getRefresh()
    ) {
      original._retried = true;

      refreshPromise ??= refreshAccessToken().finally(() => {
        refreshPromise = null;
      });

      const token = await refreshPromise;

      if (token) {
        original.headers.set('Authorization', `Bearer ${token}`);
        return api.request(original);
      }

      notifyAuthFailure();
    }

    return Promise.reject(toApiError(error));
  },
);

function toApiError(error: AxiosError<ApiErrorBody>): ApiError {
  if (!error.response) {
    return new ApiError({
      code: 'NETWORK',
      message:
        'Bağlantı kurulamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.',
      status: 0,
    });
  }

  const body = error.response.data;

  if (body && typeof body === 'object' && 'error' in body) {
    return new ApiError({
      code: body.error.code,
      message: body.error.message,
      status: error.response.status,
      fields: body.error.fields,
    });
  }

  return new ApiError({
    code: 'INTERNAL',
    message: 'Beklenmeyen bir hata oluştu.',
    status: error.response.status,
  });
}

// --- Zarf açıcılar --------------------------------------------------------
// Servis katmanı `data`yı doğrudan döner; component zarf görmez.

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.get<ApiSuccess<T>>(url, config);
  return response.data.data;
}

export async function getPaginated<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<Paginated<T>> {
  const response = await api.get<ApiSuccess<T[]>>(url, config);
  return {
    items: response.data.data,
    meta: response.data.meta ?? {
      page: 1,
      pageSize: response.data.data.length,
      total: response.data.data.length,
      pageCount: 1,
      hasNext: false,
      hasPrev: false,
    },
  };
}

export async function post<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await api.post<ApiSuccess<T>>(url, body, config);
  return response.data.data;
}

export async function patch<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await api.patch<ApiSuccess<T>>(url, body, config);
  return response.data.data;
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.delete<ApiSuccess<T>>(url, config);
  return response.data.data;
}
